import { useEffect, useMemo, useState } from "react";
import {
  ArrowRightLeft,
  Ban,
  MapPin,
  Mail,
  UserMinus,
  Users,
} from "lucide-react";
import { useAdminEventDetails, useAdminEventTables, useReassignBooking } from "../api";
import {
  useSendUserEmails,
  EmailTemplate,
} from "../api/useAdminEmails";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";

/**
 * Self-sufficient dialog for changing a booking's table membership.
 *
 * Open it from anywhere (button on a user row, edit-reservation flow, etc.)
 * by passing `bookingId` + `eventTimeId`. The dialog loads booking + table
 * data internally and routes every choice through the unified
 * `/admin/bookings/:id/reassign` endpoint.
 */
interface ReassignmentDialogProps {
  isOpen: boolean;
  bookingId: string | null;
  eventTimeId: string | undefined;
  onClose: () => void;
  /** Fired after a successful state change (admin-event caches are already invalidated). */
  onSuccess?: () => void;
}

type Choice =
  | { kind: "table"; tableId: string }
  | { kind: "unassign" }
  | { kind: "cant_be_seated" }
  | null;

export function ReassignmentDialog({
  isOpen,
  bookingId,
  eventTimeId,
  onClose,
  onSuccess,
}: ReassignmentDialogProps) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const reassign = useReassignBooking(eventTimeId);
  const sendUserEmails = useSendUserEmails();

  const { data: eventData, isLoading: eventLoading } =
    useAdminEventDetails(isOpen ? eventTimeId : undefined);
  const { data: tablesData, isLoading: tablesLoading } =
    useAdminEventTables(isOpen ? eventTimeId : undefined);

  const [choice, setChoice] = useState<Choice>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isOpen) {
      setChoice(null);
      setFilter("");
    }
  }, [isOpen, bookingId]);

  const booking = useMemo(() => {
    const list = (eventData?.data?.bookings as any[]) || [];
    return list.find((b: any) => b.id === bookingId) ?? null;
  }, [eventData, bookingId]);

  const userName = booking?.user
    ? `${booking.user.firstName} ${booking.user.lastName || ""}`.trim()
    : "";
  const userEmail = booking?.user?.email || "";
  const userNeighborhood: string | undefined = booking?.neighborhood?.name;
  const isCantBeSeated = booking?.status === "cant_be_seated";

  const tables = useMemo(() => {
    const tablesPayload = tablesData?.data as { tables?: any[]; assignments?: any[] } | undefined;
    const tableList = tablesPayload?.tables || (eventData?.data?.tables as any[]) || [];
    const assignments = tablesPayload?.assignments || (eventData?.data?.assignments as any[]) || [];
    const counts = new Map<string, number>();
    assignments.forEach((a: any) => {
      counts.set(a.availableTableId, (counts.get(a.availableTableId) || 0) + 1);
    });
    return tableList.map((t: any) => ({
      id: t.id as string,
      reservationName: t.reservationName as string,
      capacity: t.capacity as number,
      isLocked: t.state === "locked",
      restaurantName: t.restaurant?.name as string | undefined,
      restaurantNeighborhoodName: t.restaurant?.neighborhoodName as string | undefined,
      currentCount: counts.get(t.id) || 0,
    }));
  }, [tablesData, eventData]);

  const currentTableId: string | null = useMemo(() => {
    const tablesPayload = tablesData?.data as { tables?: any[]; assignments?: any[] } | undefined;
    const assignments = tablesPayload?.assignments || (eventData?.data?.assignments as any[]) || [];
    const match = assignments.find((a: any) => a.bookingId === bookingId);
    return match?.availableTableId ?? null;
  }, [tablesData, eventData, bookingId]);

  const currentTable = currentTableId
    ? tables.find((t) => t.id === currentTableId)
    : undefined;

  const sortedTables = useMemo(() => {
    const userN = userNeighborhood?.toLowerCase();
    return [...tables]
      .filter((t) => t.id !== currentTableId)
      .sort((a, b) => {
        const aN = a.restaurantNeighborhoodName?.toLowerCase() || "";
        const bN = b.restaurantNeighborhoodName?.toLowerCase() || "";
        const aIsUser = userN && aN === userN;
        const bIsUser = userN && bN === userN;
        if (aIsUser && !bIsUser) return -1;
        if (!aIsUser && bIsUser) return 1;
        const nCompare = aN.localeCompare(bN);
        if (nCompare !== 0) return nCompare;
        return (a.restaurantName || "").localeCompare(b.restaurantName || "");
      });
  }, [tables, currentTableId, userNeighborhood]);

  const filteredTables = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sortedTables;
    return sortedTables.filter(
      (t) =>
        t.reservationName?.toLowerCase().includes(q) ||
        t.restaurantName?.toLowerCase().includes(q) ||
        t.restaurantNeighborhoodName?.toLowerCase().includes(q)
    );
  }, [sortedTables, filter]);

  const isLoading = eventLoading || tablesLoading;

  const promptAndSendEmail = async (
    evtId: string,
    userId: string,
    name: string,
    template: EmailTemplate,
    promptTitle: string,
    promptMessage: string,
    successLabel: string
  ) => {
    const confirmed = await confirm({
      title: promptTitle,
      message: promptMessage,
      confirmText: "Send email",
      cancelText: "Skip",
    });
    if (!confirmed) return;
    sendUserEmails.mutate(
      {
        eventId: evtId,
        userIds: [userId],
        template,
        dryRun: false,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(`${successLabel} sent to ${name}`);
          } else {
            toast.error(data.error || `Failed to send ${successLabel.toLowerCase()}`);
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || `Failed to send ${successLabel.toLowerCase()}`);
        },
      }
    );
  };

  const handleSubmit = () => {
    if (!bookingId || !choice) return;

    const action =
      choice.kind === "table"
        ? ({ action: "assign" as const, tableId: choice.tableId })
        : choice.kind === "unassign"
          ? ({ action: "unassign" as const })
          : ({ action: "cant_be_seated" as const });

    reassign.mutate(
      { bookingId, ...action },
      {
        onSuccess: (data) => {
          if (data?.success === false) {
            toast.error(data?.error || "Failed to update assignment");
            return;
          }
          if (!data?.data?.changed) {
            toast.success("No changes");
            onClose();
            return;
          }
          if (choice.kind === "cant_be_seated") {
            toast.success(
              data?.data?.emailSent
                ? `${userName || "User"} marked can't be seated · apology email sent`
                : `${userName || "User"} marked can't be seated`
            );
            onSuccess?.();
            onClose();
            return;
          }
          if (choice.kind === "unassign") {
            toast.success("User unassigned");
            onSuccess?.();
            onClose();
            return;
          }

          // Assign / move
          const target = tables.find((t) => t.id === choice.tableId);
          const prevTable = currentTableId
            ? tables.find((t) => t.id === currentTableId)
            : null;
          toast.success(
            currentTableId
              ? `Moved to ${target?.reservationName ?? "new table"}`
              : `Assigned to ${target?.reservationName ?? "table"}`
          );
          onSuccess?.();
          onClose();

          // The bulk dinner-details email goes out automatically when a
          // table gets locked. After-the-fact moves need a manual nudge:
          //  - Prev locked → target locked: user already knew their table,
          //    now they need the "booking updated" email.
          //  - Prev unlocked/none → target locked: user has never been told
          //    where to go — send the booking-details email.
          //  - Target unlocked: nothing to communicate yet.
          if (target?.isLocked && eventTimeId && booking?.userId) {
            const displayName = userName || "User";
            const movedBetweenLockedTables = !!prevTable?.isLocked;
            if (movedBetweenLockedTables) {
              void promptAndSendEmail(
                eventTimeId,
                booking.userId,
                displayName,
                EmailTemplate.BOOKING_UPDATED,
                "Send updated booking email?",
                `${displayName} already had a locked table. Let them know their table changed?`,
                "Updated booking email"
              );
            } else {
              void promptAndSendEmail(
                eventTimeId,
                booking.userId,
                displayName,
                EmailTemplate.BOOKING_DETAILS,
                "Send assignment email?",
                `${displayName} was added to a locked table. Send the booking details email so they know where to go?`,
                "Assignment email"
              );
            }
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update assignment");
        },
      }
    );
  };

  if (!isOpen) return null;

  const submitLabel = (() => {
    if (!choice) return "Choose an action";
    if (choice.kind === "cant_be_seated") return "Mark can't be seated";
    if (choice.kind === "unassign") return "Unassign";
    return currentTableId ? "Move user" : "Assign user";
  })();

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => (!reassign.isPending ? onClose() : undefined)}
    >
      <div
        className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-base-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg leading-tight">
                {currentTableId ? "Reassign user" : "Assign user"}
              </h3>
              {isLoading && !booking ? (
                <p className="text-sm text-base-content/60">Loading…</p>
              ) : booking ? (
                <p className="text-sm text-base-content/60 truncate">
                  <span className="font-medium">{userName || "Unknown user"}</span>
                  {userEmail ? (
                    <span className="text-base-content/40"> · {userEmail}</span>
                  ) : null}
                </p>
              ) : (
                <p className="text-sm text-error">Booking not found</p>
              )}
            </div>
          </div>

          {booking && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {userNeighborhood && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-base-200">
                  <MapPin className="w-3 h-3" />
                  {userNeighborhood}
                </span>
              )}
              {isCantBeSeated ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 text-amber-400">
                  <Ban className="w-3 h-3" />
                  Currently can't be seated
                </span>
              ) : currentTable ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-base-200">
                  <Users className="w-3 h-3" />
                  On <span className="font-semibold ml-0.5">{currentTable.reservationName}</span>
                  {currentTable.restaurantName ? ` · ${currentTable.restaurantName}` : ""}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-base-200">
                  Currently unassigned
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-3">
          {!booking ? (
            <p className="text-sm text-base-content/60">
              {isLoading ? "Loading booking…" : "Could not load booking details."}
            </p>
          ) : (
            <>
              <input
                type="text"
                placeholder="Filter tables…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="input input-bordered input-sm w-full"
              />

              <div className="space-y-1.5">
                {currentTableId && (
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      choice?.kind === "unassign"
                        ? "border-warning bg-warning/5"
                        : "border-base-300 hover:border-base-content/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reassign-choice"
                      checked={choice?.kind === "unassign"}
                      onChange={() => setChoice({ kind: "unassign" })}
                      className="radio radio-warning radio-sm"
                    />
                    <UserMinus className="w-4 h-4 text-warning shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm">Unassign from table</p>
                      <p className="text-xs text-base-content/60 truncate">
                        Free the seat. No email is sent.
                      </p>
                    </div>
                  </label>
                )}

                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    choice?.kind === "cant_be_seated"
                      ? "border-error bg-error/5"
                      : "border-base-300 hover:border-base-content/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="reassign-choice"
                    checked={choice?.kind === "cant_be_seated"}
                    onChange={() => setChoice({ kind: "cant_be_seated" })}
                    className="radio radio-error radio-sm"
                    disabled={isCantBeSeated}
                  />
                  <Ban className="w-4 h-4 text-error shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Can't be seated</p>
                    <p className="text-xs text-base-content/60 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Sends apology email · refunds ticket
                    </p>
                  </div>
                </label>

                <div className="pt-1 text-xs uppercase tracking-wide text-base-content/40">
                  {currentTableId ? "Move to" : "Assign to"}
                </div>

                {filteredTables.length === 0 ? (
                  <p className="text-sm text-base-content/50 text-center py-4">
                    {tables.length === 0 ? "No tables for this event" : "No tables match the filter"}
                  </p>
                ) : (
                  filteredTables.map((table) => {
                    const isSelected =
                      choice?.kind === "table" && choice.tableId === table.id;
                    const isFull = table.currentCount >= table.capacity;
                    const isPreferredNeighborhood =
                      !!userNeighborhood &&
                      table.restaurantNeighborhoodName?.toLowerCase() ===
                        userNeighborhood.toLowerCase();
                    return (
                      <label
                        key={table.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-base-300 hover:border-base-content/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="reassign-choice"
                          checked={isSelected}
                          onChange={() =>
                            setChoice({ kind: "table", tableId: table.id })
                          }
                          className="radio radio-primary radio-sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {table.reservationName}
                            {table.isLocked ? (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-base-content/50">
                                locked
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs text-base-content/60 truncate">
                            {table.restaurantName || "Unknown restaurant"}
                            {table.restaurantNeighborhoodName
                              ? ` · ${table.restaurantNeighborhoodName}`
                              : ""}
                            {isPreferredNeighborhood ? " · ⭐ user's area" : ""}
                          </p>
                        </div>
                        <div
                          className={`text-xs shrink-0 ${
                            isFull ? "text-error" : "text-base-content/50"
                          }`}
                        >
                          {table.currentCount}/{table.capacity}
                          {isFull ? " · full" : ""}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-5 border-t border-base-300 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={reassign.isPending}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!choice || reassign.isPending || !booking}
            className={`btn btn-sm ${
              choice?.kind === "cant_be_seated"
                ? "btn-error"
                : choice?.kind === "unassign"
                  ? "btn-warning"
                  : "btn-primary"
            }`}
          >
            {reassign.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : choice?.kind === "cant_be_seated" ? (
              <Ban className="w-4 h-4 mr-1" />
            ) : choice?.kind === "unassign" ? (
              <UserMinus className="w-4 h-4 mr-1" />
            ) : (
              <ArrowRightLeft className="w-4 h-4 mr-1" />
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
